import csiAction from "csi/action";
import Popup from "csi/action/Popup";
import Action from "csi/def/Action";
import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import ViewRef from "csi/def/ViewRef";
import Renderer from "csi/heading/Renderer";
import locale from "csi/locale";
import { C8Node } from "csi/typings";
import MessageDialog from "csi/widget/MessageDialog";
import xml from "csi/xml";
import ToggleButton from "dijit/form/ToggleButton";
import declare from "dojo/_base/declare";
import lang from "dojo/_base/lang";
import _ from "lodash";

/** Module and operation names */
const MODULE_SITEADMIN: string = "SiteAdmin";
const OPERATION_API_KEY_CONNECTOR_SETUP: string = "ExternalAPIConnectorSetupAPIKeyCredentials";
const OPERATION_OAUTH_CONNECTOR_SETUP: string = "ExternalAPIConnectorSetupOAuthCredentials";

/** Setup State Enum */
const SETUP_COMPLETED_ENUM = xml.enumCreate("ExternalAPIConnectorSetup", "Complete");

/** Auth Types */
const OAUTH2_AUTH_TYPE: string = "OAuth2Credentials";
const API_KEY_AUTH_TYPE: string = "APIKeyCredentials";
const AUTH_TYPE_OPERATION_MAP: Map<string, string> = new Map([
	[API_KEY_AUTH_TYPE, OPERATION_API_KEY_CONNECTOR_SETUP],
	[OAUTH2_AUTH_TYPE, OPERATION_OAUTH_CONNECTOR_SETUP],
]);

/** API Key attribute name */
const API_KEY_NAME: string = "KeyName";
const API_KEY: string = "Key";
const API_KEY_ADD_TO: string = "APIKeyAddTo";

/** OAuth2.0 attribute name */
const CLIENT_ID: string = "ClientId";
const CLIENT_SECRET: string = "ClientSecret";
const SCOPE: string = "Scope";
const TOKEN_URL: string = "AuthenticationServerURL";

/** ExternalAPIConnector attribute name */
const PARENT_URL: string = "ParentURL";
const AUTH_BODY_PARAMETERS: string = "AuthBodyParameters";
const AUTH_HEADER_PARAMETERS: string = "AuthHeaderParameters";
const AUTH_QUERY_PARAMETERS: string = "AuthQueryParameters";
const CONNECTOR_BODY_PARAMETERS: string = "ConnectorBodyParameters";
const CONNECTOR_HEADER_PARAMETERS: string = "ConnectorHeaderParameters";
const CONNECTOR_QUERY_PARAMETERS: string = "ConnectorQueryParameters";
const CONNECTOR_GENERAL_PROPERTIES: string = "ConnectorGeneralProperties";
const TOKEN_VALIDATION_URL: string = "TokenValidationURL";
const READ_TIMEOUT_MS: string = "ReadTimeoutMS";
const CONNECTION_TIMEOUT_MS: string = "ConnectionTimeoutMS";

const EnableRenderer = declare(Renderer, {

	/** @property {boolean} ignoreChecked - Keeps track of if canceled or authentication failed */
	ignoreChecked: false,
	
	/**
	 * @override
	 * Create Toggle button for ExternalAPIConnector.Enable
	 * Depending on Connector Setup value:
	 * Not Started : Enable is read-only in UI
	 * Completed : Enable is editable in UI. User can enable/disable the connector
	 * Failed : Enable is read-only in UI
	 */
	toHtmlStatic(ctx: Context, h: Heading, refNode: HTMLElement): boolean {
		const node = ctx.getNode();
		const self = this;
		new ToggleButton({
			showLabel: true,
			class: "csi-button-switch",
			checked: node.Enable,
			disabled: node.APISetup === SETUP_COMPLETED_ENUM ? false : true,
			onChange(val): void {
				if (self.ignoreChecked) {
					self.ignoreChecked = false;
					return;
				}
				self.onChangeEnable(ctx, h, refNode, node, this);
			},
		}).placeAt(refNode);
		return true;
	},

	onChangeEnable(ctx: Context, h: Heading, refNode: HTMLElement, node: C8Node, toggleButton: ToggleButton): boolean {
		const self = this;
		if (node.Enable === true) {
			// Disabling
			new MessageDialog({
				buttons: [MessageDialog.BUTTON_CONFIRM, MessageDialog.BUTTON_CANCEL],
				materialIcon: MessageDialog.ICON_WARNING,
				message: locale.resource("DisableConnectorPopupMessage"),
				title: locale.resource("DisableConnectorPopupTitle"),
				onResponse: function(key, params): void {
					if (key == MessageDialog.BUTTON_CONFIRM) {
						// Set enable to false here
						csiAction.invoke(ctx.view, refNode, lang.mixin(new Action(), {
							m_onAction: function(ac) {
								ac.m_writer.changeNode(node.$URL).changeAttribute(h.m_id, xml.ATTR_BOOLEAN, false);
							},
							m_postAction: "postActionRedraw",
						}));
					} else if (key == MessageDialog.BUTTON_CANCEL) {
						toggleButton.setChecked(true);
						self.ignoreChecked = true;
					}
				},
			}).show();
			return;
		}

		if (node.Enable === false) {
			// Enabling
			csiAction.invoke(
				ctx.view,
				refNode,
				lang.mixin(new Action(), {
					m_onAction(ac) {
						const externalAPIConnectorURL = node.$URL;
						const credentialsNode = ctx.cache.getNode(node.Credentials);
						const serverOperation = AUTH_TYPE_OPERATION_MAP.get(credentialsNode.$Type);
						const callMethod = ac.m_writer.callMethod(MODULE_SITEADMIN, serverOperation)
							.addParameter(PARENT_URL, xml.ATTR_CNL, externalAPIConnectorURL)
							.addParameter(TOKEN_VALIDATION_URL, xml.ATTR_STRING, node.TokenValidationURL)
							.addParameter(AUTH_BODY_PARAMETERS, xml.ATTR_STRINGMAP, credentialsNode.AuthBodyParameters)
							.addParameter(AUTH_HEADER_PARAMETERS, xml.ATTR_STRINGMAP, credentialsNode.AuthHeaderParameters)
							.addParameter(AUTH_QUERY_PARAMETERS, xml.ATTR_STRINGMAP, credentialsNode.AuthQueryParameters)
							.addParameter(CONNECTOR_BODY_PARAMETERS, xml.ATTR_STRINGMAP, node.ConnectorBodyParameters)
							.addParameter(CONNECTOR_HEADER_PARAMETERS, xml.ATTR_STRINGMAP, node.ConnectorHeaderParameters)
							.addParameter(CONNECTOR_QUERY_PARAMETERS, xml.ATTR_STRINGMAP, node.ConnectorQueryParameters)
							.addParameter(CONNECTOR_GENERAL_PROPERTIES, xml.ATTR_STRINGMAP, node.ConnectorGeneralProperties)
							.addParameter(CONNECTION_TIMEOUT_MS, xml.ATTR_INTEGER, node.ConnectionTimeoutMS)
							.addParameter(READ_TIMEOUT_MS, xml.ATTR_INTEGER, node.ReadTimeoutMS);

						if (credentialsNode.$Type === API_KEY_AUTH_TYPE) {
							callMethod
								.addParameter(API_KEY_NAME, xml.ATTR_STRING, credentialsNode.KeyName)
								.addParameter(API_KEY, xml.ATTR_STRING, credentialsNode.Key)
								.addParameter(API_KEY_ADD_TO, xml.ATTR_ENUM, credentialsNode.APIKeyAddTo);
						} else if (credentialsNode.$Type === OAUTH2_AUTH_TYPE) {
							callMethod
								.addParameter(CLIENT_ID, xml.ATTR_STRING, credentialsNode.ClientId)
								.addParameter(CLIENT_SECRET, xml.ATTR_STRING, "")
								.addParameter(TOKEN_URL, xml.ATTR_STRING, credentialsNode.AuthenticationServerURL)
								.addParameter(SCOPE, xml.ATTR_STRING, credentialsNode.Scope);
						}

						ac.waitForPublish = function(dfd, bProgressDialog): void {
							this.displayProgressDialog({
								closable: false,
								message: locale.resource("ExternalAPIConnectorLoading"),
								title: locale.resource("ExternalAPIConnectorLoadingTitle"),
							});
							this.when(dfd, lang.hitch(this, this._onPublishResponse));
						};

						ac.publishError = function(err): void {
							// set enable back to false
							toggleButton.setChecked(false);
							self.ignoreChecked = true;
							// close previous dialog
							this.closeActionDialog();

							new MessageDialog({
								buttons: [MessageDialog.BUTTON_RETRY, MessageDialog.BUTTON_CANCEL],
								materialIcon: MessageDialog.ICON_ERROR,
								message: locale.resource("EnableConnectorRetryMessage", [err.message]),
								title: locale.resource("ERROR"),
								onResponse: function(key, params): void {
									if (key == MessageDialog.BUTTON_RETRY) {
										// navigate to Connector Setup action/form
										const action = _.extend(new Popup(), {
											m_id: "ExternalAPIConnectorSetup",
											classController: "SiteAdmin.ExternalAPIConnector.ExternalAPIConnectorSetupController",
											m_postAction: "postActionRefresh",
											m_viewRef: _.extend(new ViewRef(), {
												m_id: "ExternalAPIConnectorWizard",
												m_url: node.$URL,
											}),
										});
										action.resolve();
										csiAction.invoke(ctx.view, refNode, action);
									} else if (key == MessageDialog.BUTTON_CANCEL) {
										csiAction.invoke(ctx.view, refNode, lang.mixin(new Action(), {
											m_postAction: "postActionRefresh",
										}));
									}
								},
							}).show();
						};
					},
					m_postAction: "postActionRedraw",
				}),
			);
		}
	},

});

export = EnableRenderer;